import React from 'react';
import { Link } from 'react-router-dom';
import { FaGraduationCap, FaBook, FaChartLine, FaUsers, FaAward, FaStar, FaRocket, FaHeart } from 'react-icons/fa';

const About = () => {
  const features = [
    {
      title: "Expert-Led Training",
      description: "Learn from experienced stenographers and certified instructors with years of practical experience in the field.",
      icon: <FaGraduationCap className="text-3xl" />,
      color: "from-blue-500 to-cyan-500"
    },
    {
      title: "Comprehensive Curriculum",
      description: "Structured learning path covering everything from basic shorthand to advanced stenography techniques.",
      icon: <FaBook className="text-3xl" />,
      color: "from-green-500 to-emerald-500"
    },
    {
      title: "Smart Practice Tests",
      description: "Intelligent practice sessions with adaptive difficulty and real-time performance tracking.",
      icon: <FaChartLine className="text-3xl" />,
      color: "from-purple-500 to-pink-500"
    },
    {
      title: "Progress Analytics",
      description: "Detailed insights into your learning journey with personalized improvement recommendations.",
      icon: <FaUsers className="text-3xl" />,
      color: "from-orange-500 to-red-500"
    }
  ];

  const stats = [
    { number: "10,000+", label: "Students Trained", icon: "👨‍🎓" },
    { number: "95%", label: "Success Rate", icon: "🎯" },
    { number: "50+", label: "Expert Instructors", icon: "👨‍🏫" },
    { number: "24/7", label: "Learning Support", icon: "🔄" }
  ];

  const team = [
    {
      name: "Priya Sharma",
      role: "Lead Instructor",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
      description: "15+ years experience in stenography training",
      specialty: "Speed Building & Accuracy"
    },
    {
      name: "Rahul Verma",
      role: "Senior Mentor",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
      description: "Former SSC Topper & Exam Expert",
      specialty: "Exam Strategy & Preparation"
    },
    {
      name: "Anita Desai",
      role: "Technical Director",
      image: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
      description: "Digital Learning Specialist",
      specialty: "Modern Teaching Methods"
    }
  ];

  const learningJourney = [
    { step: "1", title: "Foundation", description: "Build strong basics with guided lessons" },
    { step: "2", title: "Practice", description: "Regular practice sessions with feedback" },
    { step: "3", title: "Mastery", description: "Advanced techniques and speed building" },
    { step: "4", title: "Success", description: "Exam readiness and career preparation" }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">About Our Learning Platform</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We're on a mission to make stenography learning accessible, engaging, and effective for every aspiring student.
              Join thousands who have transformed their careers with our proven learning approach.
            </p>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-lg border border-blue-100">
                <div className="text-4xl mb-2">{stat.icon}</div>
                <div className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <FaRocket className="text-white text-xl" />
                </div>
                <h2 className="text-3xl font-bold text-gray-800">Our Learning Mission</h2>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                We believe that every student deserves access to quality education that prepares them for real-world success.
                Our platform combines traditional stenography expertise with modern learning technology.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <p className="text-gray-700">Personalized learning paths for every student</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <p className="text-gray-700">Real-time feedback and progress tracking</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <p className="text-gray-700">Community support and expert mentorship</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                alt="Students learning together"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-6 rounded-2xl shadow-xl">
                <div className="text-2xl font-bold">15+ Years</div>
                <div className="text-blue-100">Of Excellence in Education</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Why Students Love Learning With Us</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We've designed every aspect of our platform with student success in mind
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg border border-blue-100 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2"
              >
                <div className={`w-14 h-14 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Journey */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Your Learning Journey</h2>
            <p className="text-gray-600 text-lg">Step-by-step path to stenography mastery</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {learningJourney.map((step, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Meet Your Guides</h2>
            <p className="text-gray-600 text-lg">Learn from the best in the industry</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden border border-blue-100 group hover:shadow-xl transition-all duration-300">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-1">{member.name}</h3>
                  <p className="text-blue-600 font-medium mb-3">{member.role}</p>
                  <p className="text-gray-600 mb-3">{member.description}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <FaAward className="text-yellow-500" />
                    <span>{member.specialty}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl shadow-2xl p-8 md:p-12 text-white">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaHeart className="text-white text-2xl" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Start Your Success Story?</h2>
            <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
              Join our community of successful stenographers and begin your journey towards a rewarding career today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-lg"
              >
                Start Learning Free
              </Link>
              <Link
                to="/exams"
                className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-200"
              >
                Explore Courses
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;